import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Modal, Space, Spin, Table } from 'antd';
import { DashboardPageShell, DashboardPanel, DashboardPrimaryActionButton, DashboardRefreshButton, DashboardSearchInput, renderMaintTag, renderServiceTag } from '../components/dashboard';
import './forkliftsPages.css';
import { api } from '../api/http';
import { updateForkliftStatus } from '../api/forklift';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import { hourMeterApiError, validateHourMeter } from '../utils/hourMeterValidation';

function toIsoStartOfDay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const v = dateStr.trim();
  if (!v) return null;
  return `${v}T00:00:00`;
}

function normalizeForklift(raw) {
  if (!raw) return null;
  const lastMaintenanceDate =
    raw.lastMaintenanceDate ??
    raw.last_maintenance_date ??
    raw.LastMaintenanceDate ??
    raw.Last_Maintenance_Date ??
    null;

  const lastMaintenanceHour =
    raw.lastMaintenanceHour ??
    raw.last_maintenance_hour ??
    raw.LastMaintenanceHour ??
    raw.Last_Maintenance_Hour ??
    null;

  return {
    id:
      raw.id ??
      raw.forkliftId ??
      raw.ForkliftId ??
      raw.ForkliftID ??
      raw.forklift_id ??
      null,
    unitId:
      raw.unitId ??
      raw.unit_id ??
      raw.unitID ??
      raw.UnitId ??
      raw.UnitID ??
      raw.forkliftUnitId ??
      null,
    make: raw.make ?? raw.Make ?? null,
    model: raw.model ?? raw.Model ?? null,
    serialNumber:
      raw.serialNumber ??
      raw.serial ??
      raw.SerialNumber ??
      raw.serial_number ??
      raw.Serial_Number ??
      null,
    hours:
      raw.hours ??
      raw.hourMeter ??
      raw.hour_meter ??
      raw.Hours ??
      raw.HourMeter ??
      raw.Hour_Meter ??
      null,
    serviceStatus:
      raw.serviceStatus ??
      raw.service_status ??
      raw.ServiceStatus ??
      raw.service ??
      raw.Service ??
      raw.status ??
      raw.Status ??
      null,
    maintenanceStatus:
      raw.maintenanceStatus ??
      raw.maintenance_status ??
      raw.MaintenanceStatus ??
      raw.maintenance ??
      raw.Maintenance ??
      null,
    lastMaintenanceDate: lastMaintenanceDate,
    lastMaintenanceHour: lastMaintenanceHour,
    retired: !!(raw.retired ?? raw.isRetired ?? raw.Retired ?? raw.IsRetired),
  };
}

async function fetchForklifts() {
  try {
    const res = await api.get('/api/forklifts');
    const list = Array.isArray(res.data) ? res.data : [];
    return list.map((f) => normalizeForklift(f)).filter(Boolean);
  } catch (e) {
    console.error('Error loading forklifts', e);
    return [];
  }
}

export default function ForkliftsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth() || {};

  const [activeForklifts, setActiveForklifts] = useState([]);
  const [retiredForklifts, setRetiredForklifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('ACTIVE');
  const [editingForkliftId, setEditingForkliftId] = useState(null);
  const [editingValues, setEditingValues] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editHourMeterError, setEditHourMeterError] = useState('');
  const [addHourMeterError, setAddHourMeterError] = useState('');
  const [selectedForkliftId, setSelectedForkliftId] = useState(null);
  const [retiring, setRetiring] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [retiredSearchTerm, setRetiredSearchTerm] = useState('');
  const [addForkliftOpen, setAddForkliftOpen] = useState(false);
  const [addForkliftConfirmOpen, setAddForkliftConfirmOpen] = useState(false);
  const [pendingAddForkliftPayload, setPendingAddForkliftPayload] = useState(null);
  const [addingForklift, setAddingForklift] = useState(false);
  const [subscription, setSubscription] = useState({ status: 'loading', entitled: false, accessState: 'none' });

  const canManageBilling =
    String(session?.role || '').trim().toLowerCase() === 'customer' &&
    String(session?.customerAccountRole || '').trim().toUpperCase() === 'OWNER' &&
    session?.canManageBilling === true;

  const canEditServiceStatus = useMemo(() => {
    const r = String(session?.role || '').trim().toLowerCase();
    return r === 'customer';
  }, [session]);

  const loadForklifts = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchForklifts();

      const sorted = [...all].sort((a, b) =>
        String(a.unitId || '').localeCompare(String(b.unitId || ''), undefined, {
          numeric: true,
        })
      );

      const active = sorted.filter(
        (f) => String(f.serviceStatus || '').toUpperCase() !== 'RETIRED'
      );
      const retired = sorted.filter(
        (f) => String(f.serviceStatus || '').toUpperCase() === 'RETIRED'
      );

      setActiveForklifts(active);
      setRetiredForklifts(retired);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadForklifts();
  }, [loadForklifts]);

  useEffect(() => {
    let active = true;
    api.get('/api/billing/subscription')
      .then(({ data }) => {
        if (active) {
          setSubscription({
            status: data?.status ?? 'none',
            entitled: Boolean(data?.entitled),
            accessState: data?.accessState ?? 'none',
          });
        }
      })
      .catch(() => {
        if (active) setSubscription({ status: 'error', entitled: false, accessState: 'none' });
      });
    return () => { active = false; };
  }, []);

  const rowClassName = (record) =>
    record.id === selectedForkliftId ? 'ant-table-row-selected' : '';

  const startEditRow = useCallback(
    (record) => {
      if (!record) return;

      const dateVal = record.lastMaintenanceDate
        ? String(record.lastMaintenanceDate).slice(0, 10)
        : '';

      const currentStatus = String(record.serviceStatus || 'IN_SERVICE')
        .trim()
        .toUpperCase();

      setEditingForkliftId(record.id);
      setEditHourMeterError('');
      setEditingValues({
        unitId: record.unitId || '',
        make: record.make || '',
        model: record.model || '',
        serialNumber: record.serialNumber || '',
        hours: record.hours || record.hours === 0 ? String(record.hours) : '',
        serviceStatus: currentStatus || 'IN_SERVICE',
        lastMaintenanceDate: dateVal,
        lastMaintenanceHour:
          record.lastMaintenanceHour || record.lastMaintenanceHour === 0
            ? String(record.lastMaintenanceHour)
            : '',
        changeReason: '',
      });
      setSelectedForkliftId(record.id);
      setView('ACTIVE');
    },
    []
  );

  const cancelEdit = () => {
    setEditingForkliftId(null);
    setEditingValues(null);
    setSavingEdit(false);
    setRetiring(false);
    setSelectedForkliftId(null);
  };

  const saveEdit = async () => {
    if (!editingForkliftId || !editingValues) return;

    const validationError = validateHourMeter(editingValues.hours);
    setEditHourMeterError(validationError);
    if (validationError) return;

    const allForklifts = [...activeForklifts, ...retiredForklifts];
    const original = allForklifts.find((f) => f.id === editingForkliftId);

    const originalStatus = original
      ? String(original.serviceStatus || 'IN_SERVICE').trim().toUpperCase()
      : 'IN_SERVICE';
    const nextStatus = String(editingValues.serviceStatus || originalStatus)
      .trim()
      .toUpperCase();
    const statusChanged = nextStatus !== originalStatus;

    if (statusChanged && !canEditServiceStatus) {
      alert('You are not allowed to change service status.');
      return;
    }

    if (original) {
      const originalValues = {
        unitId: original.unitId || '',
        make: original.make || '',
        model: original.model || '',
        serialNumber: original.serialNumber || '',
        hours:
          original.hours || original.hours === 0
            ? String(original.hours)
            : '',
        serviceStatus: String(original.serviceStatus || 'IN_SERVICE').trim().toUpperCase(),
      };

      const noChanges =
        originalValues.unitId === editingValues.unitId &&
        originalValues.make === editingValues.make &&
        originalValues.model === editingValues.model &&
        originalValues.serialNumber === editingValues.serialNumber &&
        originalValues.hours === editingValues.hours &&
        originalValues.serviceStatus === nextStatus;

      if (noChanges) {
        const baselineChanged =
          (editingValues.lastMaintenanceDate || '') !== '' ||
          (editingValues.lastMaintenanceHour || '') !== '';
        if (!baselineChanged) {
          alert('No changes to save.');
          return;
        }
      }
    }

    const isReturnToService =
      (originalStatus === 'DOWN' || originalStatus === 'WAITING_ON_PARTS') &&
      nextStatus === 'IN_SERVICE' &&
      statusChanged;
    const hourMeterChanged =
      String(original?.hours ?? '') !== String(editingValues.hours ?? '');
    const lastMaintenanceHourChanged =
      String(original?.lastMaintenanceHour ?? '') !==
      String(editingValues.lastMaintenanceHour ?? '');
    const requiresReason = hourMeterChanged || lastMaintenanceHourChanged;
    const changeReason = String(editingValues.changeReason || '').trim();
    if (requiresReason && !changeReason) {
      alert('Reason is required when changing Hour Meter or Last Maint Hours.');
      return;
    }

    const confirmed = isReturnToService
      ? window.confirm('Are you sure you want to return this unit to service?')
      : window.confirm('Save changes to this forklift?');

    if (!confirmed) return;

    setSavingEdit(true);

    const payload = {
      unitId: editingValues.unitId ? editingValues.unitId.trim() : '',
      make: editingValues.make ? editingValues.make.trim() : null,
      model: editingValues.model ? editingValues.model.trim() : null,
      serialNumber: editingValues.serialNumber
        ? editingValues.serialNumber.trim()
        : null,
      hourMeter:
        editingValues.hours !== undefined &&
        editingValues.hours !== null &&
        editingValues.hours !== ''
          ? Number(editingValues.hours)
          : null,
      lastMaintenanceDate: toIsoStartOfDay(editingValues.lastMaintenanceDate),
      lastMaintenanceHour:
        editingValues.lastMaintenanceHour !== undefined &&
        editingValues.lastMaintenanceHour !== null &&
        editingValues.lastMaintenanceHour !== ''
          ? Number(editingValues.lastMaintenanceHour)
          : null,
      changeReason: changeReason || null,
      ...(statusChanged ? { status: nextStatus, expectedStatus: originalStatus } : {}),
    };

    try {
      let res;
      try {
        res = await api.put(`/api/forklifts/${editingForkliftId}`, payload);
      } catch (error) {
        if (error?.data?.code !== 'UNFINISHED_TECHNICIAN_WORK') throw error;
        if (!window.confirm(error.data.message)) {
          setSavingEdit(false);
          return;
        }
        res = await api.put(`/api/forklifts/${editingForkliftId}`,
          { ...payload, acknowledgeUnfinishedWork: true });
      }
      if (res && res.data) {
        await loadForklifts();
        cancelEdit();
      }
    } catch (error) {
      if (error?.data?.code === 'STALE_FORKLIFT_STATUS') {
        alert(error.data.message);
        await loadForklifts();
        cancelEdit();
        setSavingEdit(false);
        return;
      }
      console.error('Error updating forklift', error);
      const fieldError = hourMeterApiError(error);
      if (fieldError) {
        setEditHourMeterError(fieldError);
        setSavingEdit(false);
        return;
      }

      let message = 'Failed to update forklift. Please try again.';
      const serverMessage =
        error &&
        error.data &&
        typeof error.data.message === 'string' &&
        error.data.message.trim()
          ? error.data.message.trim()
          : '';

      if (serverMessage) {
        message = `Failed to update forklift. ${serverMessage}`;
      }

      alert(message);
      setSavingEdit(false);
    }
  };

  const retireForklift = async () => {
    if (!editingForkliftId) return;
    const confirmed = window.confirm(
      'Are you sure you want to retire this forklift? It will be moved to the Retired Forklifts tab.'
    );
    if (!confirmed) return;

    setRetiring(true);
    try {
      const original = activeForklifts.find((forklift) => forklift.id === editingForkliftId);
      const res = await updateForkliftStatus(editingForkliftId, 'RETIRED', { expectedStatus: original?.serviceStatus });
      if (!res.ok) {
        let message = 'Failed to retire forklift. Please try again.';
        const serverMessage =
          res &&
          res.data &&
          typeof res.data.message === 'string' &&
          res.data.message.trim()
            ? res.data.message.trim()
            : '';
        if (serverMessage) {
          message = `Failed to retire forklift. ${serverMessage}`;
        }
        alert(message);
        setRetiring(false);
        return;
      }
      await loadForklifts();
      setView('RETIRED');
      cancelEdit();
    } catch (error) {
      console.error('Error retiring forklift', error);

      let message = 'Failed to retire forklift. Please try again.';
      const serverMessage =
        error &&
        error.data &&
        typeof error.data.message === 'string' &&
        error.data.message.trim()
          ? error.data.message.trim()
          : '';
      if (serverMessage) {
        message = `Failed to retire forklift. ${serverMessage}`;
      }
      alert(message);
    } finally {
      setRetiring(false);
    }
  };

  useEffect(() => {
    if (
      location.state &&
      location.state.from === 'dashboard' &&
      location.state.unitId
    ) {
      const unitIdFromNav = location.state.unitId;
      const match = activeForklifts.find((f) => f.unitId === unitIdFromNav);
      if (match) {
        setView('ACTIVE');
        startEditRow(match);
        setTimeout(() => {
          const row = document.querySelector(`[data-row-key="${match.id}"]`);
          if (row && row.scrollIntoView) {
            row.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [location.state, activeForklifts, startEditRow]);

  const handleAddForklift = (event) => {
    event.preventDefault();

    const form = event.target;

    const unitIdInput = form.elements.namedItem('unitId');
    const makeInput = form.elements.namedItem('make');
    const modelInput = form.elements.namedItem('model');
    const serialInput = form.elements.namedItem('serialNumber');
    const hoursInput = form.elements.namedItem('hourMeter');
    const lastMaintDateInput = form.elements.namedItem('lastMaintenanceDate');
    const lastMaintHourInput = form.elements.namedItem('lastMaintenanceHour');

    const unitId = unitIdInput ? unitIdInput.value.trim() : '';
    const make = makeInput ? makeInput.value.trim() : '';
    const model = modelInput ? modelInput.value.trim() : '';
    const serialNumber = serialInput ? serialInput.value.trim() : '';
    const hoursValue = hoursInput ? hoursInput.value : '';
    const lastMaintDate = lastMaintDateInput ? String(lastMaintDateInput.value || '').trim() : '';
    const lastMaintHour = lastMaintHourInput ? String(lastMaintHourInput.value || '').trim() : '';

    if (!unitId) {
      alert('Unit ID is required.');
      return;
    }

    const validationError = validateHourMeter(hoursValue);
    setAddHourMeterError(validationError);
    if (validationError) return;

    const payload = {
      unitId,
      make: make || null,
      model: model || null,
      serialNumber: serialNumber || null,
      hourMeter: hoursValue ? Number(hoursValue) : null,
      lastMaintenanceDate: toIsoStartOfDay(lastMaintDate),
      lastMaintenanceHour: lastMaintHour ? Number(lastMaintHour) : null,
    };

    setPendingAddForkliftPayload(payload);
    setAddForkliftConfirmOpen(true);
  };

  const cancelAddForklift = () => {
    setAddHourMeterError('');
    setAddForkliftConfirmOpen(false);
    setPendingAddForkliftPayload(null);
    setAddForkliftOpen(false);
  };

  const confirmAddForklift = async () => {
    if (!pendingAddForkliftPayload || addingForklift) return;

    setAddingForklift(true);

    try {
      const res = await api.post('/api/forklifts', pendingAddForkliftPayload);
      if (res && res.data) {
        await loadForklifts();
        const form = document.getElementById('addForkliftForm');
        if (form && typeof form.reset === 'function') {
          form.reset();
        }
        setPendingAddForkliftPayload(null);
        setAddForkliftConfirmOpen(false);
        setAddForkliftOpen(false);
      }
    } catch (error) {
      console.error('Error adding forklift', error);
      const fieldError = hourMeterApiError(error);
      if (fieldError) {
        setAddHourMeterError(fieldError);
        setAddForkliftConfirmOpen(false);
        return;
      }

      let message = 'Unable to add forklift. Please try again.';
      const serverMessage =
        error &&
        error.data &&
        typeof error.data.message === 'string' &&
        error.data.message.trim()
          ? error.data.message.trim()
          : '';

      if (serverMessage) {
        message = serverMessage;
      }

      alert(message);
    } finally {
      setAddingForklift(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRetiredSearchChange = (event) => {
    setRetiredSearchTerm(event.target.value);
  };

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    await loadForklifts();
  };

  const handleRetiredSearchSubmit = async (event) => {
    event.preventDefault();
    await loadForklifts();
  };

  const handleRestore = async (record) => {
    if (!record || !record.id) return;
    const confirmed = window.confirm('Restore this forklift to the active fleet?');
    if (!confirmed) return;
    setRestoringId(record.id);
    try {
      const res = await updateForkliftStatus(record.id, 'IN_SERVICE', { expectedStatus: 'RETIRED' });
      if (!res.ok) {
        let message = 'Failed to restore forklift. Please try again.';
        const serverMessage =
          res &&
          res.data &&
          typeof res.data.message === 'string' &&
          res.data.message.trim()
            ? res.data.message.trim()
            : '';
        if (serverMessage) {
          message = `Unable to restore forklift. ${serverMessage}`;
        }
        alert(message);
        return;
      }
      await loadForklifts();
      setView('ACTIVE');
    } catch (error) {
      let message = 'Failed to restore forklift. Please try again.';
      const serverMessage =
        error &&
        error.data &&
        typeof error.data.message === 'string' &&
        error.data.message.trim()
          ? error.data.message.trim()
          : '';
      if (serverMessage) {
        message = `Unable to restore forklift. ${serverMessage}`;
      }
      alert(message);
    } finally {
      setRestoringId(null);
    }
  };

  const columns = [
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
      render: (text) => (text ? <span>{text}</span> : ''),
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Serial #',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (value) => (value || value === 0 ? value : ''),
    },
    {
      title: 'Service',
      dataIndex: 'serviceStatus',
      key: 'serviceStatus',
      render: (status) => renderServiceTag(status) || '—',
    },
    {
      title: 'Maintenance',
      dataIndex: 'maintenanceStatus',
      key: 'maintenanceStatus',
      render: (status) => renderMaintTag(status) || '—',
    },
  ];

  const retiredColumns = [
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Serial #',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (value) => (value || value === 0 ? value : ''),
    },
    {
      title: 'Retired Status',
      key: 'retiredStatus',
      render: () => renderServiceTag('RETIRED') || 'Retired',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          className="forklifts-page-table-action customer-forklifts-restore-action"
          onClick={() => handleRestore(record)}
          loading={restoringId === record.id}
        >
          Restore
        </Button>
      ),
    },
  ];

  const title = resolveActiveCustomerContext(session).customerName;

  const filteredActiveForklifts = useMemo(() => {
    if (!searchTerm) return activeForklifts;
    const term = searchTerm.toLowerCase();
    return activeForklifts.filter(
      (f) =>
        (f.unitId && f.unitId.toLowerCase().includes(term)) ||
        (f.make && f.make.toLowerCase().includes(term)) ||
        (f.model && f.model.toLowerCase().includes(term)) ||
        (f.serialNumber && f.serialNumber.toLowerCase().includes(term))
    );
  }, [activeForklifts, searchTerm]);

  const filteredRetiredForklifts = useMemo(() => {
    if (!retiredSearchTerm) return retiredForklifts;
    const term = retiredSearchTerm.toLowerCase();
    return retiredForklifts.filter(
      (f) =>
        (f.unitId && f.unitId.toLowerCase().includes(term)) ||
        (f.make && f.make.toLowerCase().includes(term)) ||
        (f.model && f.model.toLowerCase().includes(term)) ||
        (f.serialNumber && f.serialNumber.toLowerCase().includes(term))
    );
  }, [retiredForklifts, retiredSearchTerm]);

  const resetViewState = (nextView) => {
    setView(nextView);
    setEditingForkliftId(null);
    setEditingValues(null);
    setSelectedForkliftId(null);
  };

  const renderEditForm = () =>
    editingForkliftId && editingValues ? (
      <div className="customer-forklifts-edit-form">
        <p className="dashboard-modal-helper">Update unit and service details.</p>
        <section className="dashboard-modal-section" aria-labelledby="customer-forklifts-unit-details-title">
          <h3 id="customer-forklifts-unit-details-title" className="dashboard-modal-section-title">Unit details</h3>
          <div className="dashboard-modal-field-grid">
            {[
              ['Unit ID', 'unitId', 'text'],
              ['Make', 'make', 'text'],
              ['Model', 'model', 'text'],
              ['Serial Number', 'serialNumber', 'text'],
            ].map(([label, key, type]) => (
              <label className="dashboard-modal-field" key={key}>
                <span>{label}</span>
                <input
                  type={type}
                  placeholder={label}
                  value={editingValues[key]}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        </section>
        <section className="dashboard-modal-section" aria-labelledby="customer-forklifts-service-maintenance-title">
          <h3 id="customer-forklifts-service-maintenance-title" className="dashboard-modal-section-title">Service &amp; maintenance</h3>
          <div className="dashboard-modal-field-grid">
            <label className="dashboard-modal-field">
              <span>Hour Meter</span>
              <input
                type="text"
                inputMode="numeric"
                aria-invalid={!!editHourMeterError}
                aria-describedby={editHourMeterError ? 'edit-hour-meter-error' : undefined}
                placeholder="Hour Meter"
                value={editingValues.hours}
                onChange={(e) => {
                  setEditingValues((prev) => ({ ...prev, hours: e.target.value }));
                  setEditHourMeterError(validateHourMeter(e.target.value));
                }}
              />
              {editHourMeterError && <span id="edit-hour-meter-error" className="hour-meter-field-error" role="alert">{editHourMeterError}</span>}
            </label>
            <label className="dashboard-modal-field">
              <span>Service Status</span>
              <select
                disabled={!canEditServiceStatus}
                value={String(editingValues.serviceStatus || 'IN_SERVICE').trim().toUpperCase()}
                onChange={(e) => setEditingValues((prev) => ({ ...prev, serviceStatus: e.target.value }))}
              >
                <option value="IN_SERVICE">IN_SERVICE</option>
                <option value="DOWN">DOWN</option>
                <option value="WAITING_ON_PARTS">WAITING_ON_PARTS</option>
              </select>
            </label>
            <label className="dashboard-modal-field">
              <span>Last Maint Date</span>
              <input
                type="date"
                value={editingValues.lastMaintenanceDate}
                onChange={(e) => setEditingValues((prev) => ({ ...prev, lastMaintenanceDate: e.target.value }))}
              />
            </label>
            <label className="dashboard-modal-field">
              <span>Last Maint Hours</span>
              <input
                type="number"
                min="0"
                value={editingValues.lastMaintenanceHour}
                onChange={(e) => setEditingValues((prev) => ({ ...prev, lastMaintenanceHour: e.target.value }))}
              />
            </label>
            <label className="dashboard-modal-field dashboard-modal-field-wide">
              <span>Reason (required for hour edits)</span>
              <input
                type="text"
                placeholder="Reason for manual hour change"
                value={editingValues.changeReason || ''}
                onChange={(e) => setEditingValues((prev) => ({ ...prev, changeReason: e.target.value }))}
              />
            </label>
          </div>
        </section>
        <div className="dashboard-modal-actions dashboard-modal-actions-split">
          <div className="dashboard-modal-action-group dashboard-modal-action-group-danger">
            <Button danger className="forklifts-page-table-action customer-forklifts-danger-action" onClick={retireForklift} loading={retiring}>Retire Forklift</Button>
          </div>
          <div className="dashboard-modal-action-group dashboard-modal-action-group-primary">
            <Button className="forklifts-page-table-action dashboard-secondary-action-button customer-forklifts-edit-secondary-action" onClick={cancelEdit}>Cancel</Button>
            <DashboardPrimaryActionButton className="forklifts-page-table-action customer-forklifts-edit-save-action" onClick={saveEdit} loading={savingEdit}>Save</DashboardPrimaryActionButton>
          </div>
        </div>
      </div>
    ) : null;

  const renderMobileCard = (forklift, retired = false) => (
    <article
      key={forklift.id}
      className={`maintenance-forklift-mobile-card shared-selected-card${forklift.id === selectedForkliftId ? ' is-selected' : ''}`}
      onClick={() => setSelectedForkliftId(forklift.id)}
    >
      <div className="maintenance-forklift-mobile-card-header">
        <span className="maintenance-forklift-mobile-unit">{forklift.unitId || 'Unit unavailable'}</span>
        <span className="maintenance-forklift-mobile-badges">
          {retired ? renderServiceTag('RETIRED') : renderServiceTag(forklift.serviceStatus)}
          {!retired ? renderMaintTag(forklift.maintenanceStatus) : null}
        </span>
      </div>
      <div className="maintenance-forklift-mobile-details">
        <div className="maintenance-forklift-mobile-model">{[forklift.make, forklift.model].filter(Boolean).join(' ') || 'Make/model unavailable'}</div>
        <div className="maintenance-forklift-mobile-meta">
          {[forklift.serialNumber ? `Serial ${forklift.serialNumber}` : '', forklift.hours || forklift.hours === 0 ? `${forklift.hours} hrs` : ''].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="forklifts-page-mobile-actions customer-forklifts-mobile-actions">
        {retired ? (
          <DashboardPrimaryActionButton onClick={(e) => { e.stopPropagation(); handleRestore(forklift); }} loading={restoringId === forklift.id}>Restore</DashboardPrimaryActionButton>
        ) : (
          <DashboardPrimaryActionButton onClick={(e) => { e.stopPropagation(); startEditRow(forklift); }}>Edit details</DashboardPrimaryActionButton>
        )}
      </div>
    </article>
  );

  const currentSearch = view === 'ACTIVE' ? searchTerm : retiredSearchTerm;
  const currentForklifts = view === 'ACTIVE' ? filteredActiveForklifts : filteredRetiredForklifts;
  const currentColumns = view === 'ACTIVE' ? columns : retiredColumns;
  const currentPageTitle = view === 'ACTIVE' ? 'Forklifts' : 'Retired Forklifts';
  const currentHelper = view === 'ACTIVE'
    ? { desktop: 'Manage active forklifts, update unit details, and retire units when they leave service.', mobile: 'Manage active forklifts.' }
    : { desktop: 'Review retired forklifts.', mobile: 'Review retired forklifts.' };
  const subscriptionAccessState = String(subscription.accessState || 'none').toLowerCase();
  const isCanceled = subscriptionAccessState === 'canceled_entitled' ||
    String(subscription.status || '').toLowerCase() === 'canceled';
  const showBillingGuidance = canManageBilling &&
    subscription.status !== 'loading' && subscription.status !== 'error' &&
    (!subscription.entitled || isCanceled);
  const activeForkliftCount = activeForklifts.length;

  return (
    <DashboardPageShell title={title} productDashboardShell="customer" shellClassName="forklifts-page-shell customer-forklifts-page-shell">
      {showBillingGuidance ? (
        <Alert
          showIcon
          type={isCanceled ? 'info' : 'warning'}
          message={isCanceled ? 'Review your fleet before reactivation' : 'Set up your fleet before billing'}
          description={(
            <Space direction="vertical" size="small">
              <span>Active forklifts: {activeForkliftCount}</span>
              {activeForkliftCount < 1 ? (
                <span>At least one active forklift is required before Checkout.</span>
              ) : (
                <>
                  {isCanceled ? (
                    <span>Continue to Billing to review the reactivation options available for your account.</span>
                  ) : null}
                  <Button type="primary" onClick={() => navigate('/customer/billing')}>
                    Continue to Billing
                  </Button>
                </>
              )}
            </Space>
          )}
        />
      ) : null}
      <Spin spinning={loading}>
        <DashboardPanel className="forklifts-page-panel customer-forklifts-page-panel">
          <header className="forklifts-page-heading technician-forklifts-page-heading customer-forklifts-page-heading">
            <div>
              <h1>{currentPageTitle}</h1>
              <p>
                <span className="customer-forklifts-helper-desktop">{currentHelper.desktop}</span>
                <span className="customer-forklifts-helper-mobile">{currentHelper.mobile}</span>
              </p>
            </div>
            <div className="maintenance-tabs-wrap customer-forklifts-view-actions">
              <div className="fleet-toggle maintenance-tabs" role="tablist" aria-label="Customer forklift sections">
                <button type="button" role="tab" aria-selected={view === 'ACTIVE'} className="fleet-toggle-button" onClick={() => resetViewState('ACTIVE')}>Active Forklifts</button>
                <button type="button" role="tab" aria-selected={view === 'RETIRED'} className="fleet-toggle-button" onClick={() => resetViewState('RETIRED')}>Retired Forklifts</button>
              </div>
              {view === 'ACTIVE' && (
                <Button htmlType="button" className="dashboard-secondary-action-button customer-forklifts-desktop-add-action" onClick={() => setAddForkliftOpen(true)}>Add Forklift</Button>
              )}
            </div>
          </header>

          {view === 'ACTIVE' && (
            <div className="customer-forklifts-mobile-page-action">
              <DashboardPrimaryActionButton
                htmlType="button"
                type="default"
                className="dashboard-mobile-page-action-button dashboard-secondary-action-button customer-forklifts-mobile-add-action"
                onClick={() => setAddForkliftOpen(true)}
              >
                Add Forklift
              </DashboardPrimaryActionButton>
            </div>
          )}

          <section
            className={view === 'ACTIVE' ? 'customer-forklifts-list-surface' : 'customer-forklifts-list-surface customer-forklifts-retired-surface'}
            aria-label={view === 'ACTIVE' ? 'Active forklifts list' : 'Retired forklifts list'}
          >
            <div className="customer-forklifts-section-heading customer-forklifts-list-heading">
              <form onSubmit={view === 'ACTIVE' ? handleSearchSubmit : handleRetiredSearchSubmit} className="dashboard-fleet-table-toolbar maintenance-list-toolbar maintenance-list-toolbar-no-helper customer-forklifts-toolbar">
                <Space className="dashboard-fleet-table-controls" wrap>
                  <DashboardSearchInput
                    placeholder="Search forklifts"
                    value={currentSearch}
                    onChange={view === 'ACTIVE' ? handleSearchChange : handleRetiredSearchChange}
                    ariaLabel="Search forklifts"
                    className="dashboard-fleet-search maintenance-list-search"
                  />
                  <DashboardRefreshButton htmlType="submit" loading={loading} className="maintenance-list-refresh" />
                </Space>
              </form>
            </div>

            <div className="forklifts-page-table-wrap shared-dashboard-table">
              <Table
                dataSource={currentForklifts}
                columns={currentColumns}
                rowKey="id"
                pagination={false}
                size="middle"
                rowClassName={rowClassName}
                onRow={(record) => ({ onClick: () => (view === 'ACTIVE' ? startEditRow(record) : setSelectedForkliftId(record.id)) })}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: view === 'ACTIVE' ? 'No active forklifts found.' : 'No retired forklifts found.' }}
              />
            </div>

            <div className="forklifts-page-mobile-list">
              {currentForklifts.length ? currentForklifts.map((forklift) => renderMobileCard(forklift, view === 'RETIRED')) : (
                <Empty description={view === 'ACTIVE' ? 'No active forklifts found.' : 'No retired forklifts found.'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </section>
        </DashboardPanel>
      </Spin>

      <Modal
        title="Edit Forklift"
        open={Boolean(editingForkliftId && editingValues)}
        onCancel={cancelEdit}
        footer={null}
        width={760}
        centered
        className="dashboard-form-modal customer-forklifts-edit-modal"
        destroyOnHidden
      >
        {renderEditForm()}
      </Modal>
      <Modal
        title="Add Forklift"
        open={addForkliftOpen && !addForkliftConfirmOpen}
        onCancel={cancelAddForklift}
        footer={null}
        width={720}
        centered
        className="dashboard-form-modal customer-forklifts-add-modal"
        destroyOnHidden={false}
      >
        <p className="dashboard-modal-helper">Enter unit information below to add a new forklift.</p>
        <Alert
          className="dashboard-modal-warning-note"
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={<><strong>Billing notice:</strong> Adding a forklift may increase your subscription cost according to your current plan.</>}
        />
        <form id="addForkliftForm" onSubmit={handleAddForklift} className="dashboard-modal-form-grid customer-forklifts-modal-form-grid">
          <input name="unitId" placeholder="Unit ID" />
          <input name="make" placeholder="Make" />
          <input name="model" placeholder="Model" />
          <input name="serialNumber" placeholder="Serial Number" />
          <label className="dashboard-modal-field">
            <span>Hour Meter</span>
            <input name="hourMeter" placeholder="Hour Meter" type="text" inputMode="numeric"
              aria-invalid={!!addHourMeterError}
              aria-describedby={addHourMeterError ? 'add-hour-meter-error' : undefined}
              onChange={(e) => setAddHourMeterError(validateHourMeter(e.target.value))} />
            {addHourMeterError && <span id="add-hour-meter-error" className="hour-meter-field-error" role="alert">{addHourMeterError}</span>}
          </label>
          <input name="lastMaintenanceHour" placeholder="Last Maint Hours" type="number" min="0" />
          <label className="dashboard-modal-date-field"><span>Last Maint Date</span><input name="lastMaintenanceDate" type="date" /></label>
          <div className="dashboard-modal-actions customer-forklifts-add-actions">
            <Button htmlType="button" className="dashboard-secondary-action-button customer-forklifts-add-secondary-action" onClick={cancelAddForklift}>Cancel</Button>
            <DashboardPrimaryActionButton className="customer-forklifts-add-primary-action" htmlType="submit" loading={addingForklift}>Add Forklift</DashboardPrimaryActionButton>
          </div>
        </form>
      </Modal>
      <Modal
        title="Confirm forklift addition"
        open={addForkliftConfirmOpen}
        onCancel={() => setAddForkliftConfirmOpen(false)}
        footer={
          <div className="dashboard-modal-confirm-actions">
            <DashboardPrimaryActionButton className="customer-forklifts-add-primary-action" onClick={confirmAddForklift} loading={addingForklift}>Confirm</DashboardPrimaryActionButton>
            <Button className="dashboard-secondary-action-button customer-forklifts-add-secondary-action" onClick={() => setAddForkliftConfirmOpen(false)} disabled={addingForklift}>Cancel</Button>
          </div>
        }
        width={480}
        centered
        className="dashboard-form-modal dashboard-confirm-modal customer-forklifts-add-confirm-modal"
        destroyOnHidden
      >
        <Alert
          className="dashboard-modal-warning-note is-flush"
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Adding this forklift may increase your subscription cost according to your current plan. Please confirm that you want to add this forklift to your active fleet."
        />
      </Modal>
    </DashboardPageShell>
  );
}
